import type { CustomerOrderView } from "@pufimi/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { Check, CircleAlert, Clock, ExternalLink, PawPrint, Truck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPriceCents } from "@/features/orders/pricing";
import { ApiError, api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { appShellTitle } from "@/lib/seo/head";
import { cn } from "@/lib/utils";

// InPost's public parcel-tracking page; the buyer's own tracking number is the
// query. Single-carrier PL MVP (ADR-0021 rev.), so the link is InPost's.
const INPOST_TRACKING_URL = "https://inpost.pl/sledzenie-przesylek?number=";

type LoadState = "loading" | "loaded" | "notFound" | "error";

// Locale-aware date (+ short time) for a ladder stamp / a bare day for "placed on";
// both go through the shared memoized `formatDate` (never throws in render).
const formatWhen = (iso: string, lang: string) =>
  formatDate(iso, lang, { dateStyle: "medium", timeStyle: "short" });
const formatDay = (iso: string, lang: string) => formatDate(iso, lang, { dateStyle: "medium" });

type CustomerOrderLine = CustomerOrderView["lines"][number];

// The localized product name for one line (no server-side copy — the projection
// carries only the shape + product type). A pillow maps its shape; a shapeless line
// (a 3D print, ADR-0054 #409) falls back to a localized product-TYPE label — never a
// hardcoded string, so a new type is one i18n key, not a code change.
function lineName(line: CustomerOrderLine, t: TFunction): string {
  if (line.shape) return t(`orderView.product.${line.shape}`);
  if (line.productType) return t(`orderView.productType.${line.productType}`, line.productType);
  return t("orderView.productType.unknown");
}

// Lines grouped by their per-order group ordinal (the projection assigns one per source
// project), preserving order — so the page lists every line grouped by pet (#409).
function groupLinesByProject(
  lines: CustomerOrderLine[],
): { group: number; lines: CustomerOrderLine[] }[] {
  const byGroup = new Map<number, CustomerOrderLine[]>();
  for (const line of lines) {
    const bucket = byGroup.get(line.projectGroup) ?? [];
    bucket.push(line);
    byGroup.set(line.projectGroup, bucket);
  }
  return [...byGroup.entries()]
    .sort(([a], [b]) => a - b)
    .map(([group, groupLines]) => ({ group, lines: groupLines }));
}

// The customer-facing Order view (ADR-0051, #314): a calm, no-PII status page for
// one order, reached from the confirmation email under a stable capability token.
// The pet render is the hero; the coarse 4-stop ladder carries the status; the
// one warm thread (Studio Coral) marks the current stop. It never shows the
// delivery destination, receiver, phone, or email (they never cross the wire).
function OrderView() {
  const { token } = Route.useParams();
  const { t, i18n } = useTranslation("orders");
  const [state, setState] = useState<LoadState>("loading");
  const [view, setView] = useState<CustomerOrderView | null>(null);
  // Bumped by the retry button to re-run the fetch effect.
  const [reloadKey, setReloadKey] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is the retry TRIGGER — a bump re-runs the fetch; it is intentionally not read in the body.
  useEffect(() => {
    // `active` guards against a stale response overwriting a newer token's state
    // if the param changes mid-flight (the fetch has no abort signal).
    let active = true;
    setState("loading");
    api.orders.view(token).then(
      (v) => {
        if (active) {
          setView(v);
          setState("loaded");
        }
      },
      (err) => {
        if (active) {
          setView(null);
          setState(err instanceof ApiError && err.code === "not_found" ? "notFound" : "error");
        }
      },
    );
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  if (state === "notFound") {
    return (
      <section className="mx-auto flex max-w-md flex-col items-center gap-6 px-2 py-16 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <CircleAlert className="size-6" aria-hidden />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-balance">
            {t("orderView.notFound.title")}
          </h1>
          <p className="mt-2 text-balance text-sm text-muted-foreground">
            {t("orderView.notFound.body")}
          </p>
        </div>
        <Button asChild size="lg" className="min-h-12 rounded-full px-8 font-semibold">
          <Link to="/">{t("orderView.notFound.back")}</Link>
        </Button>
      </section>
    );
  }

  if (state === "error") {
    return (
      <section className="mx-auto flex max-w-md flex-col items-center gap-4 px-2 py-16 text-center">
        <p className="text-sm text-muted-foreground">{t("orderView.loadError")}</p>
        <Button type="button" variant="outline" onClick={() => setReloadKey((k) => k + 1)}>
          {t("orderView.loadRetry")}
        </Button>
      </section>
    );
  }

  return (
    <section
      className="mx-auto flex w-full max-w-xl flex-col gap-6 py-4"
      data-testid="order-view"
      data-status={view?.status}
    >
      <header className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t("orderView.title")}</h1>
        <p className="mt-1 text-balance text-sm text-muted-foreground">{t("orderView.lead")}</p>
      </header>

      {state === "loading" || !view ? (
        <OrderViewSkeleton />
      ) : (
        <OrderViewBody view={view} t={t} lang={i18n.language} />
      )}
    </section>
  );
}

function OrderViewBody({ view, t, lang }: { view: CustomerOrderView; t: TFunction; lang: string }) {
  const hero = view.lines[0];
  const banner =
    view.status === "pending" || view.status === "cancelled" || view.status === "refunded"
      ? view.status
      : null;

  return (
    <>
      {/* Hero: the pet render is the emotional center (DESIGN.md — the pet is the
          hero); a quiet placeholder holds the square so the layout never jumps. For a
          multi-line order this leads with the first item; every line is listed below. */}
      <ThumbnailHero url={hero?.thumbnailUrl ?? null} alt={t("orderView.imageAlt")} t={t} />

      <div className="text-center">
        <p className="text-xs text-muted-foreground tabular-nums">
          {t("orderView.orderRef", { id: view.id.slice(0, 8) })} ·{" "}
          {t("orderView.placedOn", { date: formatDay(view.createdAt, lang) })}
        </p>
      </div>

      {banner ? <StateBanner state={banner} t={t} /> : null}

      {/* Every purchased line, grouped by pet (#409). */}
      <ItemsCard view={view} t={t} />

      {/* Status ladder — the coarse 4 stops, the one place coral leads. */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="mb-5 text-sm font-semibold tracking-tight text-muted-foreground uppercase">
          {t("orderView.statusTitle")}
        </h2>
        <StatusLadder view={view} t={t} lang={lang} />
      </div>

      {view.tracking ? (
        <TrackingCard number={view.tracking.number} carrier={view.tracking.carrier} t={t} />
      ) : null}

      <AmountsCard view={view} t={t} />

      <p className="px-4 text-center text-xs text-muted-foreground text-balance">
        {t("orderView.footer")}
      </p>
    </>
  );
}

// Every purchased line, grouped by pet (#409): one group heading per source project,
// each line a compact row (thumbnail, product name, size · quantity, per-line price).
// Non-PII throughout — a thumbnail + a product name + amounts, nothing forwardable.
function ItemsCard({ view, t }: { view: CustomerOrderView; t: TFunction }) {
  const money = (cents: number) => formatPriceCents(cents, view.currency);
  // Precompute a stable positional key per line (a line has no id on the non-PII
  // wire, and the fetched-once list never reorders) so the render below never keys
  // off a raw array index.
  const groups = groupLinesByProject(view.lines).map((g) => ({
    group: g.group,
    lines: g.lines.map((line, i) => ({ line, key: `${g.group}-${i}` })),
  }));
  return (
    <div
      className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6"
      data-testid="order-view-items"
    >
      <h2 className="text-sm font-semibold tracking-tight text-muted-foreground uppercase">
        {t("orderView.itemsTitle")}
      </h2>
      {groups.map(({ group, lines }) => (
        <div key={group} className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <PawPrint className="size-3.5" aria-hidden />
            <span>{t("orderView.groupTitle")}</span>
          </div>
          {lines.map(({ line, key }) => (
            <div key={key} className="flex items-center gap-3" data-testid="order-view-line">
              <LineThumbnail url={line.thumbnailUrl} alt={t("orderView.imageAlt")} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="truncate text-sm font-medium" data-testid="order-view-product">
                  {lineName(line, t)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("orderView.sizeLabel", { size: line.sizeCode, cm: line.sizeCm })} ·{" "}
                  {t("orderView.quantityLabel", { count: line.quantity })}
                </p>
              </div>
              <span className="shrink-0 text-sm tabular-nums" data-testid="order-view-line-price">
                {money(line.unitPriceCents * line.quantity)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// A small square thumbnail for one line row — the rendered keepsake, or a quiet paw
// placeholder until the render exists (mirrors the hero, at row scale).
function LineThumbnail({ url, alt }: { url: string | null; alt: string }) {
  return (
    <div className="size-14 shrink-0 overflow-hidden rounded-xl border border-border bg-muted">
      {url ? (
        <img src={url} alt={alt} className="size-full object-contain" />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          <PawPrint className="size-5 opacity-60" aria-hidden />
        </div>
      )}
    </div>
  );
}

function ThumbnailHero({ url, alt, t }: { url: string | null; alt: string; t: TFunction }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-muted">
      {url ? (
        <img
          src={url}
          alt={alt}
          onLoad={() => setLoaded(true)}
          className={cn(
            "aspect-square w-full object-contain transition-opacity duration-500 ease-out",
            loaded ? "opacity-100" : "opacity-0",
          )}
          data-testid="order-view-thumbnail"
        />
      ) : (
        <div
          className="flex aspect-square w-full flex-col items-center justify-center gap-2 text-muted-foreground"
          data-testid="order-view-thumbnail-placeholder"
        >
          <PawPrint className="size-8 opacity-60" aria-hidden />
          <span className="text-sm">{t("orderView.imagePending")}</span>
        </div>
      )}
    </div>
  );
}

// The vertical 4-stop stepper. Each stop's node is coral-filled with a check once
// done; the current stop wears a soft coral halo and a coral label (the "one warm
// thread"); upcoming stops stay quiet. The connector into each stop turns coral
// once that stop is reached. Auto-sizes to content — no magic pixel offsets.
function StatusLadder({ view, t, lang }: { view: CustomerOrderView; t: TFunction; lang: string }) {
  return (
    <ol className="flex flex-col" data-testid="order-view-ladder">
      {view.ladder.map((stop, i) => {
        const isLast = i === view.ladder.length - 1;
        // The current stop is also `done` (rank-monotonic), so announce "in
        // progress" only for a non-terminal current stop; a delivered order's
        // final stop is complete, not in progress.
        const a11y =
          stop.current && !isLast
            ? t("orderView.ladder.currentA11y")
            : stop.done
              ? t("orderView.ladder.doneA11y")
              : t("orderView.ladder.upcomingA11y");
        return (
          <li
            key={stop.status}
            className="flex gap-4"
            data-testid={`ladder-stop-${stop.status}`}
            data-done={stop.done}
            data-current={stop.current}
          >
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "relative flex size-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                  stop.done
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground",
                  stop.current && "ring-4 ring-primary/15",
                )}
              >
                {stop.done ? (
                  <Check className="size-4" aria-hidden strokeWidth={3} />
                ) : (
                  <span className="size-2 rounded-full bg-current opacity-50" aria-hidden />
                )}
              </span>
              {!isLast ? (
                <span
                  className={cn(
                    "my-1 w-0.5 flex-1",
                    view.ladder[i + 1]?.done ? "bg-primary" : "bg-border",
                  )}
                  aria-hidden
                />
              ) : null}
            </div>
            <div className={cn("min-w-0", isLast ? "pb-0" : "pb-7")}>
              <p
                className={cn(
                  "font-medium leading-none",
                  stop.current
                    ? "text-primary"
                    : stop.done
                      ? "text-foreground"
                      : "text-muted-foreground",
                )}
              >
                {t(`orderView.ladder.${stop.status}`)}
                <span className="sr-only"> — {a11y}</span>
              </p>
              {stop.at ? (
                <time
                  dateTime={stop.at}
                  className="mt-1.5 block text-xs text-muted-foreground tabular-nums"
                >
                  {formatWhen(stop.at, lang)}
                </time>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// The pending / cancelled / refunded banner. Pending is a calm neutral note (the
// webhook confirms payment asynchronously); cancelled / refunded use the muted
// alert tone (a full tint, never a side stripe — DESIGN.md).
function StateBanner({ state, t }: { state: "pending" | "cancelled" | "refunded"; t: TFunction }) {
  const terminal = state === "cancelled" || state === "refunded";
  const Icon = state === "pending" ? Clock : CircleAlert;
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border p-4 text-sm",
        terminal
          ? "border-alert/30 bg-alert/5 text-foreground"
          : "border-border bg-muted text-foreground",
      )}
      role="status"
      data-testid={`order-view-banner-${state}`}
    >
      <Icon
        className={cn("mt-0.5 size-5 shrink-0", terminal ? "text-alert" : "text-muted-foreground")}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="font-medium">{t(`orderView.state.${state}.title`)}</p>
        <p className="mt-0.5 text-muted-foreground">{t(`orderView.state.${state}.body`)}</p>
      </div>
    </div>
  );
}

function TrackingCard({
  number,
  carrier,
  t,
}: {
  number: string;
  carrier: string | null;
  t: TFunction;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-6"
      data-testid="order-view-tracking"
    >
      <div className="flex items-center gap-2 font-medium">
        <Truck className="size-5 text-primary" aria-hidden />
        {t("orderView.tracking.title")}
      </div>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">
            {t("orderView.tracking.numberLabel")}
            {carrier ? ` · ${carrier}` : ""}
          </p>
          <p
            className="truncate font-mono text-sm tabular-nums"
            data-testid="order-view-tracking-number"
          >
            {number}
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="shrink-0">
          <a
            href={`${INPOST_TRACKING_URL}${encodeURIComponent(number)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("orderView.tracking.cta")}
            <ExternalLink className="size-4" aria-hidden />
          </a>
        </Button>
      </div>
    </div>
  );
}

function AmountsCard({ view, t }: { view: CustomerOrderView; t: TFunction }) {
  const money = (cents: number) => formatPriceCents(cents, view.currency);
  return (
    <div
      className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-6 text-sm"
      data-testid="order-view-amounts"
    >
      <h2 className="mb-1 text-sm font-semibold tracking-tight text-muted-foreground uppercase">
        {t("orderView.detailsTitle")}
      </h2>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">{t("orderView.amounts.product")}</span>
        <span className="tabular-nums">{money(view.productAmountCents)}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">{t("orderView.amounts.shipping")}</span>
        <span className="tabular-nums">{money(view.shippingAmountCents)}</span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-3 font-medium">
        <span>{t("orderView.amounts.total")}</span>
        <span className="tabular-nums" data-testid="order-view-total">
          {money(view.totalAmountCents)}
        </span>
      </div>
    </div>
  );
}

function OrderViewSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-hidden data-testid="order-view-skeleton">
      <Skeleton className="aspect-square w-full rounded-2xl" />
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-4 w-40" />
      </div>
      <Skeleton className="h-52 w-full rounded-2xl" />
      <Skeleton className="h-32 w-full rounded-2xl" />
    </div>
  );
}

export const Route = createFileRoute("/order/$token")({
  head: () => ({ meta: [{ title: appShellTitle(i18n.t("shells.order")) }] }),
  component: OrderView,
});
